import qs from 'qs';
import { APIService } from '../../api/apiService';
import type {
  GetGrantByRecipientPayload,
  GetGrantByRecipientResponse,
  GetGrantPayload,
  GetGrantsPayload,
  GetGrantsResponse,
  ProcessGrantPayload,
} from '../../interfaces/GrantApiService';

/**
 * Service class for performing operations related to grants.
 *
 * Handles API calls to manage file grants, including retrieving pending grants, fetching grant details,
 * querying grants by recipient, and listing recipients. Inherits basic request logic from APIService.
 */
export class GrantService extends APIService {
  /**
   * Retrieves a paginated list of pending grants with optional filtering.
   *
   * @param payload - Filtering and pagination options:
   *   - offset: number (pagination)
   *   - count: number (results per page)
   *   - field?: string (optional sort/search field)
   *   - recipientUserId?: number (specific user filter)
   *   - recipientGroupId?: number (specific group filter)
   * @returns Promise resolving to the grants list from the server API.
   */
  getGrants(payload: GetGrantsPayload): Promise<GetGrantsResponse> {
    const queryString = qs.stringify(payload, { skipNulls: true });
    return this.request(`/v3/file/grants?${queryString}`, 'GET');
  }

  /**
   * Retrieves a specific grant by its ID, indicating whether it's for a group or a user.
   *
   * @param payload - Object containing:
   *   - grantId: number (the ID of the grant)
   *   - isGroup: boolean (true if the grant is for a group, false if for a user)
   * @returns Promise resolving to the grant details from the server API.
   */
  getGrant(payload: GetGrantPayload): Promise<any> {
    return this.request(`/v3/file/grants/${payload.grantId}?isGroup=${payload.isGroup}`, 'GET');
  }

  /**
   * Fetches grants by recipient user or group.
   *
   * @param payload - Object allowing:
   *   - recipientUserId?: number (filter by user, optional)
   *   - recipientGroupId?: number (filter by group, optional)
   * @returns Promise resolving to the grant details with files matching the recipient criteria.
   */
  getGrantByRecipient(payload: GetGrantByRecipientPayload): Promise<GetGrantByRecipientResponse> {
    const queryString = qs.stringify(payload, { skipNulls: true });
    return this.request(`/v3/file/grants/by-recipient?${queryString}`, 'GET');
  }

  /**
   * Fetches the list of all grant recipients, optionally filtered by status.
   *
   * @param status - String reflecting the status to filter recipients by (e.g. "active", "pending").
   * @returns Promise resolving to the list of grant recipients matching the status.
   */
  getGrantRecipients(status: string): Promise<any> {
    return this.request(`/v3/file/grants/recipients?status=${status}`, 'GET');
  }

  /**
   * Processes a grant.
   *
   * @param payload - Object containing:
   *   - grants: Array of grants to process.
   *   - grantId: The unique identifier of the grant.
   *   - approved: Boolean indicating if the grant is approved.
   *   - message: (Optional) Message to accompany the approval or rejection.
   */
  processGrant(payload: ProcessGrantPayload): Promise<any> {
    return this.request(`/v3/file/grants/process`, 'POST', { ...payload });
  }
}
